import { useCallback, useRef, useState } from "react";
import { Search } from "lucide-react";
import clsx from "clsx";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/Button";
import type { BookingDeparture } from "@/types/camping";

export interface AccessKeyLookupFieldProps {
  departureDate?: string;
  onMatch: (booking: BookingDeparture) => void;
  onNoMatch?: () => void;
  className?: string;
  disabled?: boolean;
}

export function AccessKeyLookupField({
  departureDate,
  onMatch,
  onNoMatch,
  className,
  disabled,
}: AccessKeyLookupFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async () => {
    const q = query.trim();
    if (!q) return;

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ q });
      if (departureDate) {
        params.set("departure_date", departureDate);
      }
      const matches = await api.get<BookingDeparture[]>(
        `/api/v1/bookings/access-lookup?${params.toString()}`,
      );
      if (matches.length === 0) {
        setError("Keine eingecheckte Buchung mit diesem Transponder oder dieser Schlüsselnummer gefunden.");
        onNoMatch?.();
        return;
      }
      if (matches.length > 1) {
        setError(`${matches.length} Treffer – bitte Buchung in der Liste wählen.`);
      }
      onMatch(matches[0]!);
      setQuery("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Suche fehlgeschlagen.");
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [departureDate, onMatch, onNoMatch, query]);

  return (
    <div className={clsx("space-y-2", className)}>
      <label className="block text-sm">
        <span className="mb-1 block font-medium text-label">Transponder / Schlüsselnummer</span>
        <div className="flex gap-2">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-label-tertiary" />
            <input
              ref={inputRef}
              className="eh-input pl-9"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                if (error) setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void search();
                }
              }}
              placeholder="Scannen oder Nummer eingeben…"
              disabled={disabled || loading}
              autoComplete="off"
            />
          </div>
          <Button type="button" variant="secondary" loading={loading} disabled={disabled || !query.trim()} onClick={() => void search()}>
            Suchen
          </Button>
        </div>
      </label>
      {error ? <p className="text-sm text-status-orange">{error}</p> : null}
    </div>
  );
}
